// ===== RECAP CARD PRO =====
// 
// SISTEMA DE OPTIMIZACIÓN:
// 1. Los datos se cargan cuando se abre el modal (openRecapModal)
// 2. Si la cuenta NO cambió → usa caché (misma cuenta, mismos datos)
// 3. Si la cuenta SÍ cambió → invalida caché y recalcula
// 4. Cambiar filtro (day/week) → solo actualiza la vista (NO recalcula)
//
// Flujo de cambio de cuenta:
// app.js changeAccount() → invalidateRecapCache() → openRecapModal() → calculateRealRecapMetrics() → recalcula

(function() {
    'use strict';

let recapCurrentFilter = 'day';
let recapData = {
    day: {
        profit: 0,
        profitPercent: 0,
        winrate: 0,
        trades: 0,
        maxWin: 0,
        maxLoss: 0,
        status: 'neutral'
    },
    week: {
        profit: 0,
        profitPercent: 0,
        winrate: 0,
        trades: 0,
        maxWin: 0,
        maxLoss: 0,
        status: 'neutral'
    }
};

// Cache para evitar cálculos innecesarios
let cachedAccountId = null;
let cachedAccountBalance = null;

// Función helper para forzar recálculo (se llama cuando cambia de cuenta)
function invalidateRecapCache() {
    cachedAccountId = null;
    cachedAccountBalance = null;
}

// Exponer al window para que app.js pueda acceder
window.invalidateRecapCache = invalidateRecapCache;

// Función para obtener las métricas reales del journal desde variables globales
function calculateRealRecapMetrics() {
    const today = new Date();
    const startOfWeek = new Date(today);
    // 🆕 Calcular correctamente el lunes de esta semana
    // getDay(): 0=domingo, 1=lunes, ..., 6=sábado
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // cuántos días atrás es lunes
    startOfWeek.setDate(today.getDate() - daysToMonday);

    // Extraer fecha formateada igual que app.js
    const formatDateKey = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const todayKey = formatDateKey(today);

    try {
        // Acceder directamente a window.journalData (exponido desde app.js)
        const journalData = window.journalData;
        const accounts = window.accounts;
        const currentAccount = window.currentAccount;
        
        if (!journalData || typeof journalData !== 'object') {
            return;
        }

        // Obtener el balance inicial de la cuenta actual
        const accountBalance = accounts && accounts[currentAccount] 
            ? (accounts[currentAccount].initial_balance || accounts[currentAccount].balance || 100000)
            : 100000;

        // Actualizar cache
        cachedAccountId = currentAccount;
        cachedAccountBalance = accountBalance;

        // Calcular métricas del día
        const dayTrades = journalData[todayKey] || [];
        const dayMetrics = {
            profit: 0,
            wins: 0,
            trades: dayTrades.length,
            maxWin: 0,
            maxLoss: 0
        };

        dayTrades.forEach(trade => {
            const profit = parseFloat(trade.profitUSD) || 0;
            dayMetrics.profit += profit;
            if (profit > 0) dayMetrics.wins++;
            if (profit > dayMetrics.maxWin) dayMetrics.maxWin = profit;
            if (profit < dayMetrics.maxLoss) dayMetrics.maxLoss = profit;
        });

        // Calcular métricas de la semana
        const weekMetrics = {
            profit: 0,
            wins: 0,
            trades: 0,
            maxWin: 0,
            maxLoss: 0
        };

        // Crear fechas limpias para evitar mutación
        const todayClean = new Date();
        
        const mondayDate = new Date(todayClean);
        const dayOfWeekClean = mondayDate.getDay();
        const daysToMonday = dayOfWeekClean === 0 ? 6 : dayOfWeekClean - 1;
        mondayDate.setDate(mondayDate.getDate() - daysToMonday);
        mondayDate.setHours(0, 0, 0, 0); // Inicio del lunes
        
        // Calcular el domingo de esta semana (no solo hasta hoy)
        const sundayDate = new Date(mondayDate);
        sundayDate.setDate(sundayDate.getDate() + 6); // +6 días = domingo
        sundayDate.setHours(23, 59, 59, 999); // Fin del domingo

        // Iterar desde el inicio de la semana hasta el domingo
        const weekDate = new Date(mondayDate);
        while (weekDate <= sundayDate) {
            const dateKey = formatDateKey(weekDate);
            const trades = journalData[dateKey] || [];
            
            trades.forEach(trade => {
                weekMetrics.trades++;
                const profit = parseFloat(trade.profitUSD) || 0;
                weekMetrics.profit += profit;
                if (profit > 0) weekMetrics.wins++;
                if (profit > weekMetrics.maxWin) weekMetrics.maxWin = profit;
                if (profit < weekMetrics.maxLoss) weekMetrics.maxLoss = profit;
            });
            
            // Avanzar al siguiente día
            weekDate.setDate(weekDate.getDate() + 1);
        }

        // Actualizar recapData
        recapData.day = {
            profit: dayMetrics.profit,
            profitPercent: (dayMetrics.profit / accountBalance) * 100,
            winrate: dayMetrics.trades > 0 ? (dayMetrics.wins / dayMetrics.trades) * 100 : 0,
            trades: dayMetrics.trades,
            maxWin: dayMetrics.maxWin,
            maxLoss: dayMetrics.maxLoss,
            status: dayMetrics.profit > 0 ? 'positive' : dayMetrics.profit < 0 ? 'negative' : 'neutral'
        };

        recapData.week = {
            profit: weekMetrics.profit,
            profitPercent: (weekMetrics.profit / accountBalance) * 100,
            winrate: weekMetrics.trades > 0 ? (weekMetrics.wins / weekMetrics.trades) * 100 : 0,
            trades: weekMetrics.trades,
            maxWin: weekMetrics.maxWin,
            maxLoss: weekMetrics.maxLoss,
            status: weekMetrics.profit > 0 ? 'positive' : weekMetrics.profit < 0 ? 'negative' : 'neutral'
        };

    } catch (error) {
        // Error silencioso
    }
}

function openRecapModal() {
    // VERIFICAR ACCESO A RECAP POR PLAN
    const userPlan = (localStorage.getItem('userPlan') || 'free').toLowerCase();
    const userType = localStorage.getItem('userType') || 'user';
    
    if (userType !== 'admin' && !['pro', 'unlimited'].includes(userPlan)) {
        // Mostrar notificación pero no bloquear completamente - permitir visualizar estadísticas básicas
        // pero sin la funcionalidad de compartir
    }
    
    // FORZAR recálculo de métricas cada vez que se abre el modal
    invalidateRecapCache();
    calculateRealRecapMetrics();
    
    // Seleccionar "Hoy" por defecto
    selectRecapFilter('day');
    
    const modal = document.getElementById('recapModal');
    
    if (modal) {
        modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        updateRecapDisplay();
    } else {
    }
}

function closeRecapModal() {
    const modal = document.getElementById('recapModal');
    if (modal) {
        modal.classList.add('hidden');
        document.body.style.overflow = 'auto';
    }
}

function selectRecapFilter(filter) {
    recapCurrentFilter = filter;
    
    // Update active button styles (Tailwind) in modal header
    const dayBtn = document.getElementById('recapFilterDay');
    const weekBtn = document.getElementById('recapFilterWeek');
    
    if (filter === 'day') {
        dayBtn.className = 'px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-500/25';
        weekBtn.className = 'px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all bg-slate-800 hover:bg-slate-700 text-slate-300';
    } else {
        weekBtn.className = 'px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-500/25';
        dayBtn.className = 'px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all bg-slate-800 hover:bg-slate-700 text-slate-300';
    }
    
    // Update display
    updateRecapDisplay();
}

function updateRecapDisplay() {
    const data = recapData[recapCurrentFilter];
    
    // Calcular fecha apropiada
    let periodText = '';
    let footerDate = '';
    
    if (recapCurrentFilter === 'day') {
        periodText = 'HOY';
        footerDate = new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } else {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const monday = new Date(today.setDate(diff));
        const sundayOfWeek = new Date(today.setDate(today.getDate() + 6));
        
        const mondayStr = monday.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
        const sundayStr = sundayOfWeek.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
        
        periodText = 'ESTA SEMANA';
        footerDate = `Semana del ${mondayStr} al ${sundayStr}`;
    }
    
    const isPositive = data.profitPercent > 0;
    const isNegative = data.profitPercent < 0;
    const profitDisplay = data.profit >= 0 ? `+$${data.profit.toFixed(2)}` : `-$${Math.abs(data.profit).toFixed(2)}`;
    const percentageDisplay = data.profitPercent >= 0 ? `+${data.profitPercent.toFixed(2)}%` : `${data.profitPercent.toFixed(2)}%`;
    
    const recapCard = document.getElementById('recapCard');
    if (recapCard) {
        // Apply card styling directly to recapCard element
        recapCard.className = 'relative bg-gradient-to-br from-slate-800 via-slate-900/60 to-slate-950 backdrop-blur-xl rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg overflow-hidden w-full';
        
        recapCard.innerHTML = `
                
                <!-- Content -->
                <div class="relative z-10">
                    <!-- Header -->
                    <div class="flex items-center justify-between mb-6 sm:mb-8">
                        <div class="flex items-center gap-3">
                            <img src="./utils/logo.png" alt="TFX" class="h-[60px] sm:h-20 w-auto" style="transform: scale(1.5);" onerror="this.outerHTML='<div class=\'text-2xl sm:text-3xl font-black bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent\'>TFX</div>'">
                            <div>
                                <div class="text-sm sm:text-base font-black text-white">TripulacionFX</div>
                                <div class="text-xs text-slate-400">Trading Journal</div>
                            </div>
                        </div>
                        <div class="text-right">
                            <div class="text-xs font-black text-white uppercase tracking-wider">${periodText}</div>
                            <div class="text-xs text-slate-500">${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        </div>
                    </div>

                    <!-- Performance Hero -->
                    <div class="relative bg-gradient-to-br ${isPositive ? 'from-emerald-500/10 via-emerald-500/5 to-transparent' : isNegative ? 'from-red-500/10 via-red-500/5 to-transparent' : 'from-slate-500/10 via-slate-500/5 to-transparent'} rounded-2xl p-4 sm:p-6 mb-6 text-center">
                        <!-- Icon Flotante -->
                        <div class="absolute -top-3 left-1/2 -translate-x-1/2">
                            <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${isPositive ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' : isNegative ? 'bg-gradient-to-br from-red-400 to-red-600' : 'bg-gradient-to-br from-slate-400 to-slate-600'} flex items-center justify-center shadow-2xl">
                                ${isPositive ? 
                                    '<svg class="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>' : 
                                isNegative ? 
                                    '<svg class="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/></svg>' :
                                    '<svg class="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 12h14"/></svg>'
                                }
                            </div>
                        </div>
                        
                        <div class="mt-10 sm:mt-12">
                            <div class="text-xs sm:text-sm text-slate-300 font-semibold mb-2 uppercase tracking-wide">Balance ${recapCurrentFilter === 'day' ? 'Diario' : 'Semanal'}</div>
                            <div class="text-4xl sm:text-5xl font-black mb-2 ${isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-slate-400'} tracking-tight">${percentageDisplay}</div>
                            <div class="text-base sm:text-lg font-bold ${isPositive ? 'text-emerald-300' : isNegative ? 'text-red-300' : 'text-slate-300'}">
                                ${profitDisplay}
                            </div>
                        </div>
                    </div>

                    <!-- Stats Grid -->
                    <div class="grid grid-cols-2 gap-3 sm:gap-4">
                        <!-- Operaciones -->
                        <div class="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5">
                            <div class="flex items-center justify-between mb-2">
                                <div class="text-xs sm:text-sm text-slate-400 font-bold uppercase tracking-wider">Operaciones</div>
                                <svg class="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                            </div>
                            <div class="text-3xl sm:text-4xl font-black text-white">${data.trades}</div>
                        </div>
                        
                        <!-- Win Rate -->
                        <div class="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-blue-500/30">
                            <div class="flex items-center justify-between mb-2">
                                <div class="text-xs sm:text-sm text-blue-300 font-bold uppercase tracking-wider">Win Rate</div>
                                <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </div>
                            <div class="text-3xl sm:text-4xl font-black text-blue-400">${data.winrate.toFixed(0)}%</div>
                        </div>
                        
                        <!-- Mejor Trade -->
                        <div class="bg-gradient-to-br from-emerald-500/10 to-green-500/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-emerald-500/30">
                            <div class="flex items-center justify-between mb-2">
                                <div class="text-xs sm:text-sm text-emerald-300 font-bold uppercase tracking-wider">Mejor Trade</div>
                                <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>
                            </div>
                            <div class="text-2xl sm:text-3xl font-black text-emerald-400">+$${data.maxWin.toFixed(2)}</div>
                        </div>
                        
                        <!-- Peor Trade -->
                        <div class="bg-gradient-to-br from-red-500/10 to-pink-500/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-red-500/30">
                            <div class="flex items-center justify-between mb-2">
                                <div class="text-xs sm:text-sm text-red-300 font-bold uppercase tracking-wider">Peor Trade</div>
                                <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/></svg>
                            </div>
                            <div class="text-2xl sm:text-3xl font-black text-red-400">${data.maxLoss >= 0 ? '+' : ''}$${Math.abs(data.maxLoss).toFixed(2)}</div>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div class="mt-6 pt-5 flex flex-col items-start gap-3">
                        <div class="text-xs text-slate-500">${footerDate}</div>
                        <div class="flex items-center gap-2">
                            <span class="text-xs sm:text-sm text-slate-400 font-semibold">tripulacionfx.com</span>
                        </div>
                    </div>
                </div>
        `;
    }
}

function downloadRecapImage(format) {
    const recapCard = document.getElementById('recapCard');
    if (!recapCard) return;

    // Disable button temporarily
    const btn = event.target;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Generando...';

    // Check if html2canvas is loaded
    if (typeof html2canvas !== 'undefined') {
        html2canvas(recapCard, {
            backgroundColor: null,
            scale: 2,
            logging: false,
            allowTaint: true,
            useCORS: true
        }).then(canvas => {
            const link = document.createElement('a');
            link.href = canvas.toDataURL(`image/${format}`);
            const fileName = `MonkeyDFX-Recap-${new Date().toISOString().split('T')[0]}.${format}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            btn.disabled = false;
            btn.textContent = originalText;
        }).catch(err => {
            showError('Error al generar la imagen. Intenta de nuevo.');
            btn.disabled = false;
            btn.textContent = originalText;
        });
    } else {
        showError('La librería para exportar no está cargada. Por favor recarga la página.');
        btn.disabled = false;
        btn.textContent = originalText;
    }
}

function downloadRecapPDF() {
    const recapCard = document.getElementById('recapCard');
    if (!recapCard) return;

    const btn = event.target;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Generando...';

    if (typeof html2canvas !== 'undefined' && typeof jsPDF !== 'undefined') {
        html2canvas(recapCard, {
            backgroundColor: '#1a1a2e',
            scale: 2,
            logging: false,
            allowTaint: true,
            useCORS: true
        }).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const imgWidth = 180;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 15, 15, imgWidth, imgHeight);
            pdf.save(`MonkeyDFX-Recap-${new Date().toISOString().split('T')[0]}.pdf`);

            btn.disabled = false;
            btn.textContent = originalText;
        }).catch(err => {
            showError('Error al generar el PDF. Intenta de nuevo.');
            btn.disabled = false;
            btn.textContent = originalText;
        });
    } else {
        showError('Las librerías para exportar no están cargadas. Por favor recarga la página.');
        btn.disabled = false;
        btn.textContent = originalText;
    }
}

function copyRecapToClipboard() {
    const recapCard = document.getElementById('recapCard');
    if (!recapCard) return;

    const btn = event.target;
    const originalText = btn.textContent;
    btn.textContent = '✓ Copiado!';

    if (typeof html2canvas !== 'undefined') {
        html2canvas(recapCard, {
            backgroundColor: null,
            scale: 2,
            logging: false,
            allowTaint: true,
            useCORS: true
        }).then(canvas => {
            canvas.toBlob(blob => {
                const item = new ClipboardItem({ 'image/png': blob });
                navigator.clipboard.write([item]).then(() => {
                    setTimeout(() => {
                        btn.textContent = originalText;
                    }, 2000);
                }).catch(err => {
                    btn.textContent = originalText;
                });
            });
        });
    }
}

// Close modal with Escape key
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
        const modal = document.getElementById('recapModal');
        if (modal && modal.classList.contains('active')) {
            closeRecapModal();
        }
    }
});

// Close modal on overlay click
document.addEventListener('click', (e) => {
    const modal = document.getElementById('recapModal');
    if (modal && e.target === modal) {
        closeRecapModal();
    }
});

// Expose functions to global scope
window.openRecapModal = openRecapModal;
window.closeRecapModal = closeRecapModal;
window.selectRecapFilter = selectRecapFilter;
window.downloadRecapImage = downloadRecapImage;
window.copyRecapToClipboard = copyRecapToClipboard;

})(); // End IIFE
